import {
  InvalidDigitCountError,
  normalizeSteuernummer,
  parseSteuernummer,
  InvalidStateCodeError as SteuernummerInvalidStateCodeError,
  validateSteuernummer,
} from "@fideus/steuernummer";
import { z } from "zod/v4";
import { InvalidStateCodeError } from "./errors/InvalidStateCodeError";
import { InvalidTaxIdLengthError } from "./errors/InvalidTaxIdLengthError";
import type { StateCode } from "./federalStates";

// The steuernummer library cannot disambiguate states that share
// a single-digit prefix (3 → BB/SN/ST, 4 → TH/MV). The 2-digit prefix of
// the Bundesfinanzamtnummer uniquely identifies the state.
const bufaPrefixToState: Record<string, StateCode> = {
  "30": "DE-BB",
  "31": "DE-ST",
  "32": "DE-SN",
  "40": "DE-TH",
  "41": "DE-MV",
};

export class TaxId {
  static readonly schema = z.preprocess(
    (value, ctx) => {
      if (value instanceof TaxId) {
        return value;
      }
      if (typeof value !== "string") {
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: typeof value,
        });
        return z.NEVER;
      }

      try {
        return TaxId.fromNationalId(value);
      } catch (error) {
        if (error instanceof Error) {
          ctx.addIssue({
            code: "custom",
            message: error.message,
            params: { value },
          });
          return z.NEVER;
        }
        throw error;
      }
    },
    z.custom<TaxId>((value) => value instanceof TaxId)
  );

  readonly #taxId: string;
  private constructor(taxId: string) {
    const validationErrorMessage = validateSteuernummer(taxId);
    if (validationErrorMessage) {
      throw new Error(validationErrorMessage);
    }
    this.#taxId = taxId;
  }

  static fromNationalId(taxId: string): TaxId {
    return new TaxId(taxId);
  }

  static fromFederalIdAndFederalState(
    federalId: string,
    state: StateCode
  ): TaxId {
    try {
      return TaxId.fromNationalId(federalId);
    } catch {}
    try {
      return new TaxId(normalizeSteuernummer(federalId, state));
    } catch (error) {
      if (error instanceof InvalidDigitCountError) {
        throw new InvalidTaxIdLengthError(state, error.expectedLength);
      }
      if (error instanceof SteuernummerInvalidStateCodeError) {
        throw new InvalidStateCodeError(state);
      }
      throw error;
    }
  }

  public get taxId(): string {
    return this.#taxId;
  }

  public toString(): string {
    return this.#taxId;
  }

  public get states(): StateCode[] {
    const { states, bundesfinanzamtnummer } = parseSteuernummer(
      this.#taxId,
      {}
    );
    const allStates = Array.isArray(states) ? states : [states];
    if (allStates.length <= 1) return allStates;

    const bufaPrefix = bundesfinanzamtnummer.slice(0, 2);
    const disambiguated = bufaPrefixToState[bufaPrefix];
    if (disambiguated) return [disambiguated];

    return allStates;
  }

  public get taxOfficeNumber(): string {
    const { bundesfinanzamtnummer } = parseSteuernummer(this.#taxId, {});
    return bundesfinanzamtnummer;
  }

  public get federalTaxId(): string {
    const {
      bezirksnummer,
      bundesfinanzamtnummer,
      pruefziffer,
      statePrefix,
      unterscheidungsnummer,
    } = parseSteuernummer(this.#taxId, {});
    let taxOfficeLocalPart = this.removeStateFromBuFa(
      bundesfinanzamtnummer,
      statePrefix
    );
    // Hessen tax office local part needs a leading 0
    if (statePrefix === "26") {
      taxOfficeLocalPart = taxOfficeLocalPart.padStart(3, "0");
    }
    return statePrefix === "28"
      ? `${taxOfficeLocalPart}${bezirksnummer}/${unterscheidungsnummer}${pruefziffer}`
      : `${taxOfficeLocalPart}/${bezirksnummer}/${unterscheidungsnummer}${pruefziffer}`;
  }

  static serialise(taxId: TaxId): string {
    return taxId.taxId;
  }

  static deserialise(taxId: string): TaxId {
    return TaxId.fromNationalId(taxId);
  }

  private removeStateFromBuFa(buFaNumber: string, statePrefix: string) {
    if (buFaNumber.startsWith("10")) {
      return buFaNumber.replace("10", "");
    }
    if (buFaNumber.startsWith("40")) {
      return buFaNumber.replace("40", "");
    }
    return buFaNumber.replace(statePrefix, "");
  }

  public equals(other: unknown): boolean {
    return other instanceof TaxId && this.taxId === other.taxId;
  }
}
